/**
 * What the Meta tab says, for each of the three ways a brand can stand.
 *
 * Pulled out of the component because it is the part that was WRONG and the
 * part worth asserting. There is no testing-library here, so logic left inside
 * a `.tsx` is only reachable from the browser suite - see `unmatchedAds.ts` for
 * the same split.
 *
 * The bug: `connected` meant `brands.meta_connected`, which records only that a
 * brand finished Facebook OAuth. Partner-access brands never do that, so Alan's
 * own brand - ad account wired, 755 ads and EUR 707 of spend in the warehouse -
 * was shown **"Not Connected - Action needed"** above a panel listing 57 of his
 * ads, with a button offering to connect him to an app nobody administers.
 *
 * @module web/screens/app/metaPanel
 */

export type MetaConnectionKind = "oauth" | "system_user" | "none";

export interface MetaPanelInput {
  connected: boolean;
  connectionKind: MetaConnectionKind;
  adAccountId: string | null;
  adAccountName: string | null;
  partnerBusinessId: string | null;
  /** When they pressed "Ping us", ISO, or null if they never have. */
  sharePingedAt?: string | null;
}

export interface MetaPanelView {
  /**
   * True once the client has asked us to finish. Step 4 becomes an
   * acknowledgement and the button is not rendered at all.
   */
  pinged: boolean;
  heading: string;
  detail: string;
  /**
   * `live` is "in progress, and not on you" - the orange pulsing pill. It is
   * its own tone because "Action needed" in amber next to a line saying there
   * is nothing more for you to do is a contradiction, and the badge is the most
   * visible thing on the card.
   */
  pill: { text: string; tone: "success" | "warn" | "live" };
  /** The ad account row is only worth showing when there is one. */
  showAccount: boolean;
  /**
   * Whether to offer the Facebook OAuth button.
   *
   * False for `system_user`, and that is the heart of the fix: pressing it sent
   * a brand that is ALREADY connected into an OAuth flow against the abandoned
   * app, whose only admin is somebody nobody on the team knows.
   */
  showConnectButton: boolean;
  showDisconnect: boolean;
  /** The steps a client follows to share their ad account, when there is work to do. */
  steps: string[];
}

/**
 * The three things only the client can do, and then the one that summons us.
 *
 * Step 4 is a real step in the list rather than a note under the button,
 * because it IS one: after granting partner access nothing works yet, and a
 * client who stops at step 3 believes they are finished. Numbering it puts the
 * remaining work on screen instead of leaving them to wonder why no numbers
 * appeared.
 */
const PARTNER_STEPS = (businessId: string | null): string[] => [
  "In your own Meta Business Settings, open Users → Partners → Add partner.",
  businessId
    ? `Enter Aditor's business ID: ${businessId}`
    : "Enter Aditor's business ID - ask us for it and we'll send it over.",
  'Give that partner your ad account with "View performance" access only.',
  PING_STEP,
];

/**
 * Step 4, and the button that performs it.
 *
 * It used to read "Tell us, and we'll finish the last step on our side" - with
 * no way to tell us. It is an action now, because the last step genuinely is
 * ours: their grant alone reads nothing until an Aditor admin assigns the
 * account to the `perf-reader` system user, and `perf-reader` is an EMPLOYEE
 * so it cannot do that for itself. This is how a human hears about it.
 */
/**
 * One line, like the three above it.
 *
 * The reassurance used to be a second paragraph under the step, which made
 * step 4 a two-block item in a list of one-line ones - and the two blocks were
 * one thought split in half. Steps 1-3 are each a sentence; so is this.
 */
export const PING_STEP =
  "Ping us to complete the setup - we'll assign it on our side and your numbers appear here.";
export const TELL_US_LABEL = "Ping us";

/**
 * A short walkthrough video (Cap) that shows the client the share steps above.
 * The embed renders directly under "How to share your ad account" ONLY when this
 * is set - an empty string means no video, so we never ship a blank iframe. This
 * is the full EMBED url (Cap: cap.so/embed/<id>, share url cap.so/s/<id>).
 */
export const META_SETUP_VIDEO_URL = "https://cap.so/embed/a1ry1bjb8qd54fs";

/**
 * What step 4 says once they have pressed it. One line, for the same reason.
 *
 * The button is GONE at this point, not disabled-with-a-tick. There is nothing
 * useful a second press can do - the team has the message - and a live-looking
 * control on a screen still headed "Action needed" invites one.
 *
 * "We're on it, as fast as we can" belongs to the card's own detail line rather
 * than being repeated here. Said in both places it read as padding: the panel
 * told you three times, in three sizes, that we were working on it.
 */
export const PINGED_TITLE = "Got it - your message reached the team.";

/** "You told us on 26 Aug." - so a long wait is visible rather than just silent. */
export function pingedOnLabel(iso: string | null | undefined, locale?: string): string | null {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  // A short month is abbreviated WITH a full stop in some locales - German
  // renders "26. Aug." - so appending one produced "26. Aug..". Trimmed rather
  // than dropped, because the sentence needs its own terminator in the locales
  // that do not supply one.
  const date = when.toLocaleDateString(locale, { day: "numeric", month: "short" }).replace(/\.$/, "");
  return `You told us on ${date}.`;
}

/**
 * What to draw.
 *
 * The `system_user` case says connected and offers no button, because there is
 * nothing for the client to press: the connection was made in their Business
 * Manager and finished by an Aditor admin.
 */
export function metaPanelView(status: MetaPanelInput | null): MetaPanelView {
  const kind = status?.connectionKind ?? "none";
  const partnerId = status?.partnerBusinessId ?? null;

  if (kind === "system_user") {
    return {
      // NOT "Connected". The pill beside this already says Connected, and a
      // third copy sat in the details block below - the same fact three times
      // on one card. The heading labels the card; the pill carries the state.
      heading: "Your ad account",
      detail: "We're reading this ad account through the access your Business Manager granted us.",
      // Connected: there is no step 4 left to acknowledge.
      pinged: false,
      pill: { text: "Connected", tone: "success" },
      showAccount: true,
      showConnectButton: false,
      // Nothing to disconnect from this side - access is revoked where it was
      // granted, in the client's own Business Manager.
      showDisconnect: false,
      steps: [],
    };
  }

  if (kind === "oauth") {
    return {
      heading: "Your ad account",
      detail: "Your Meta ad account is linked.",
      pinged: false,
      pill: { text: "Connected", tone: "success" },
      showAccount: true,
      showConnectButton: true,
      showDisconnect: true,
      steps: [],
    };
  }

  // Told us, but not wired yet: the work is real and it is OURS. Saying
  // "Action needed" here asks the client to do something that does not exist.
  if (status?.sharePingedAt) {
    return {
      heading: "Almost there",
      detail: "You've done your part. We're finishing the setup on our side as fast as we can.",
      pinged: true,
      pill: { text: "We're on it", tone: "live" },
      showAccount: false,
      showConnectButton: false,
      showDisconnect: false,
      // The steps stay: they are still what happened, and step 4 becomes the
      // acknowledgement rather than a button.
      steps: PARTNER_STEPS(partnerId),
    };
  }

  return {
    heading: "Not connected",
    detail: "Share your ad account with us and your ad performance appears here.",
    pinged: false,
    pill: { text: "Action needed", tone: "warn" },
    // An ad account id with kind "none" would be a half-finished state; showing
    // it would suggest something is wired when nothing can be read.
    showAccount: false,
    showConnectButton: false,
    showDisconnect: false,
    steps: PARTNER_STEPS(partnerId),
  };
}

/** What the Account row shows: a name if we have one, else the id, else nothing. */
export function accountLabel(status: MetaPanelInput | null): string {
  return status?.adAccountName ?? status?.adAccountId ?? "-";
}
